import '@vaadin/dialog';
// eslint-disable-next-line no-duplicate-imports
import { DialogOpenedChangedEvent } from '@vaadin/dialog';
import { dialogFooterRenderer, dialogRenderer } from '@vaadin/dialog/lit.js';
import { type TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { helpFilledIcon } from '../../assets/icons';
import { TmBaseComponent } from '../../tm-base.component';

export const tagName = 'tm-form-row';
@customElement(tagName)
export class TmFormRow extends TmBaseComponent {
    @property()
    public label?: string | TemplateResult;

    @property()
    public helperText?: string | TemplateResult;

    @property({ type: Boolean, reflect: true })
    public required = false;

    @property({ type: Boolean, reflect: true })
    public noWrap = false;

    @property({ type: Boolean, reflect: true, attribute: 'items-start' })
    public itemsStart = false;

    @property({ type: Boolean, reflect: true })
    public withHelperText = false;

    @property({ type: Boolean, reflect: true })
    public noMarginRight = false;

    protected readonly verifiedPropNames: (keyof this)[] = ['label'];

    @state()
    private hasSummary = false;

    @state()
    private helperDialogOpened = false;

    protected render() {
        return html`
            ${this.renderHelperDialog()}
            <div
                class=${classMap({
                    'flex text-sm': true,
                    'mr-4': !this.noMarginRight,
                    'items-center': !this.itemsStart,
                    'items-start': this.itemsStart,
                })}
            >
                <span
                    class=${classMap({
                        'mt-2': !this.itemsStart,
                        'mt-7': this.itemsStart,
                        'mr-2': true,
                        'mb-2': !this.withHelperText,
                        'mb-5': this.withHelperText,
                        'whitespace-nowrap': this.noWrap,
                    })}
                >
                    <span
                        class="font-medium form-row-label"
                        @click=${() =>
                            this.helperText ? (this.helperDialogOpened = true) : nothing}
                        >${this.label}${this.renderHelperDialogIconBtn()}</span
                    ></span
                >
                <span class="text-secondary font-light">
                    <slot name="sublabel"></slot>
                </span>
                <slot class="flex-grow" @slotchange=${this.inputElementChange}></slot>
            </div>
            <slot name="extra"></slot>
            <div
                class=${classMap({
                    'border-dashed': this.hasSummary,
                    'border-t': this.hasSummary,
                    'mr-4': true,
                })}
            >
                <slot name="summary" class="text-right" @slotchange=${this.handleSlotChange}></slot>
            </div>
        `;
    }

    private handleSlotChange(e: Event) {
        if (e.target instanceof HTMLSlotElement) {
            this.hasSummary = e.target?.assignedNodes({ flatten: true }).length > 0;
        }
    }

    private inputElementChange(e: Event) {
        if (!(e.target instanceof HTMLSlotElement)) {
            return;
        }

        e.target.assignedElements().forEach(element => {
            element.classList.add('form-row-slotted', 'ml-auto');
            element.setAttribute('formRowSlotted', '');
            if (this.required) {
                element.setAttribute('required', '');
            } else {
                element.removeAttribute('required');
            }
        });
    }

    private renderHelperDialog() {
        if (!this.helperText) {
            return nothing;
        }
        return html`<vaadin-dialog
            id="helper-text-dialog"
            .opened=${this.helperDialogOpened}
            @opened-changed=${(event: DialogOpenedChangedEvent) => {
                this.helperDialogOpened = event.detail.value;
            }}
            ${dialogRenderer(
                () =>
                    html`<h2 class="text-lg font-bold">${this.label}</h2>
                        ${this.helperText}`
            )}
            ${dialogFooterRenderer(() => {
                return html`<vaadin-button
                    @click="${() => (this.helperDialogOpened = false)}"
                    style="align-self: flex-end;"
                >
                    Close
                </vaadin-button>`;
            })}
        ></vaadin-dialog>`;
    }

    private renderHelperDialogIconBtn() {
        if (!this.helperText) {
            return nothing;
        }
        return html`<button class="ml-1" }>${helpFilledIcon}</button>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: flex;
                    flex-direction: column;
                    margin-left: var(--tm-form-row-margin-left, 1rem);
                }

                :host([dashed]) {
                    border-bottom-style: dashed !important;
                }

                :not([name])::slotted(*) {
                    width: 100%;
                }

                button svg {
                    height: 14px !important;
                    width: 14px !important;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmFormRow;
    }
}
